// docs/60 DC14 in the encounter HUD: the dungeon crawl's card. The crawl's name and why the party is
// down here, where the selected (else first) hero stands — its depth and chamber — how deep the
// party has come, the lairs woken and the roamers that found it, the pulses left before a stalemate,
// and the journal of lore read (unread fragments by title only). Every word is the sim's or the
// storyteller's (`Snapshot.crawl`, the party's view); this file only lays them out.
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Modal, confirmDialog } from '../components';
import { rpc } from '../../core/net/reducers';
import { useCrawlForce } from '../../core/subscriptions/hooks';
import { useEncounter, useShownSnapshot } from '../../core/encounter/session';
import { useAuthoring } from '../../core/stores/authoring';
import { useCrawl } from '../../core/stores/crawl';
import { deviceNow } from '../../core/time';
import { FORCE_XP, briefingOf, chambersEntered, depthName, depthNumber, depthsReached, forceableDoors, lairsWoken, whereIs } from '../../core/crawl/crawl';
import type { CrawlView, Snapshot } from '../../core/sim/types';
import './crawl.css';

/** The card itself, for the HUD and the `?crawl=1` gallery (which passes its own board). */
export function CrawlCard({ board, latest, compact = false }: { board?: Snapshot; latest?: Snapshot; compact?: boolean }) {
  const shown = useShownSnapshot();
  const now = useEncounter((s) => s.snapshot);
  const selected = useAuthoring((s) => s.selectedHero);
  const snap = board ?? shown;
  // the journal reads the latest board: the playback flashes a fragment as it is read
  const crawl = snap?.crawl;
  const journalCrawl = (latest ?? now)?.crawl ?? crawl;
  const [open, setOpen] = useState(!compact);
  // docs/61: an exploration carries a crawl for its reaches; its own card shows it
  if (!snap || !crawl || snap.explore) return null;
  const hero = snap.units.find((u) => u.heroId !== undefined && u.heroId === selected && u.onBoard !== false) ?? snap.units.find((u) => u.heroId !== undefined && u.onBoard !== false);
  const where = hero ? whereIs(crawl, snap.board.w, hero.tile) : {};
  const depths = depthsReached(crawl), rooms = chambersEntered(crawl);
  const woken = lairsWoken(crawl);
  const found = crawl.roamers.filter((r) => r.engaged).length;
  const left = Math.max(0, crawl.pulseLimit - snap.pulseIndex);
  return (
    <section className="fk-hud__crawl fk-hit" data-testid="crawl-card" aria-label={`The crawl: ${crawl.name}`}
      data-depth={where.depth?.index} data-area={where.area?.index} data-depths={`${depths.entered}/${depths.total}`} data-lairs={woken}>
      <button type="button" className="fk-hud__crawl-head" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="crawl-toggle">
        <h3 className="fk-hud__label">{crawl.name || 'The crawl'}</h3>
        <span className="fk-hud__crawl-count" title="Depths the party has set foot on">{depths.entered} / {depths.total} depths</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {!board && <ForceDoor />}
      {open && <>
        {crawl.quest && <p className="fk-hud__crawl-quest" data-testid="crawl-quest"><span className="fk-hud__crawl-eyebrow">A stop on the quest</span> {crawl.quest}</p>}
        {crawl.premise && <p className="fk-hud__crawl-premise" data-testid="crawl-premise">{crawl.premise}</p>}
        {crawl.hook && <p className="fk-hud__crawl-hook" data-testid="crawl-hook">{crawl.hook}</p>}
        <Button small variant="ghost" onClick={() => useCrawl.getState().setBrief(true)} data-testid="crawl-brief-open">Briefing</Button>
        <div className="fk-hud__crawl-where" data-testid="crawl-where">
          <span className="fk-hud__crawl-eyebrow">{hero ? `${hero.name} is in` : 'The party is in'}</span>
          <b className="fk-hud__crawl-depth">{where.depth ? `${depthName(crawl, where.depth.index)}` : 'the dark'}</b>
          {where.depth && <span className="fk-hud__crawl-level">{depthNumber(crawl, where.depth.index) === undefined ? 'below the depths' : `depth ${depthNumber(crawl, where.depth.index)} of ${depths.total}`}</span>}
          {where.area?.name && <span className="fk-hud__crawl-room">{where.area.name}</span>}
        </div>
        <ol className="fk-hud__crawl-depths" aria-label="Depths">
          {crawl.depths.map((d, i) => (d.aside && !d.entered) ? null : (
            <li key={i} className={`fk-hud__crawl-d ${d.entered ? 'is-entered' : ''} ${where.depth?.index === i ? 'is-here' : ''}`} title={d.entered ? d.text : 'Not yet reached'}>
              <span className="fk-hud__crawl-pip" aria-hidden="true" />{d.entered ? d.name : `Depth ${depthNumber(crawl, i)}`}
            </li>
          ))}
        </ol>
        <p className="fk-hud__crawl-stats">
          <Badge tone="grey">{rooms.entered} chamber{rooms.entered === 1 ? '' : 's'} explored</Badge>{' '}
          <Badge tone={woken ? 'red' : 'grey'}>{woken} lair{woken === 1 ? '' : 's'} woken</Badge>{' '}
          {found > 0 && <Badge tone="red">{found} prowler{found === 1 ? '' : 's'} on your trail</Badge>}{' '}
          <Badge tone={left < 20 ? 'red' : 'grey'} title={`A crawl is a stalemate after ${crawl.pulseLimit} turns`}>{left} turns left</Badge>
        </p>
        <Journal crawl={journalCrawl ?? crawl} />
      </>}
    </section>
  );
}

/**
 * docs/118 PT5: the way past any stuck door. When one of my heroes stands beside a shut door or gate
 * of the crawl, the card offers to force it: it gives way at the end of the turn, and every hero's
 * reward for the crawl falls by `FORCE_XP` (never below its floor), as a puzzle room's Force rung
 * does. Always shown, even with the card folded, so a stuck party finds it.
 */
function ForceDoor() {
  const snap = useEncounter((s) => s.snapshot);
  const encounterId = useEncounter((s) => s.encounterId);
  const controlled = useEncounter((s) => s.controlled);
  const selected = useAuthoring((s) => s.selectedHero);
  const row = useCrawlForce(encounterId);
  const [busy, setBusy] = useState(false);
  if (!snap?.crawl || snap.explore || encounterId === undefined) return null;
  const pending = row?.pending ?? [];
  const doors = forceableDoors(snap, controlled, selected, pending);
  const giving = (snap.fixtures ?? []).filter((f) => pending.includes(f.id) && !f.on);
  if (!doors.length && !giving.length && !row?.debtXp) return null;
  const pick = doors[0];
  const force = async () => {
    if (!pick) return;
    if (!(await confirmDialog({
      eyebrow: 'Force the door', title: `Break ${pick.door} open?`,
      text: `${pick.hero} forces it: it gives way at the end of this turn. Every hero's reward for the crawl falls by ${FORCE_XP} XP.`,
      confirmLabel: 'Force it', danger: true,
    }))) return;
    setBusy(true);
    await rpc.forceDoor(encounterId, BigInt(pick.heroId), pick.fixture);
    setBusy(false);
  };
  return (
    <div className="fk-hud__crawl-force" data-testid="crawl-force" data-debt={row?.debtXp ?? 0} data-forced={row?.forced.length ?? 0}>
      {pick && (
        <Button small variant="danger" loading={busy} onClick={() => void force()} data-testid="crawl-force-door" data-fixture={pick.fixture}
          title={`${pick.hero} breaks ${pick.door} open at the end of this turn; the crawl then pays ${FORCE_XP} XP less to each hero`}>
          Force {pick.door} · −{FORCE_XP} XP
        </Button>
      )}
      {giving.map((f) => <span key={f.id} className="fk-hud__crawl-forcing">{cap(f.name)} gives way at the end of this turn.</span>)}
      {!!row?.debtXp && <span className="fk-hud__crawl-forced">{row.forced.length} door{row.forced.length === 1 ? '' : 's'} forced · −{row.debtXp} XP each</span>}
    </div>
  );
}

/** The journal: what the party has read on the crawl's walls, in the order it lies; unread by title. */
function Journal({ crawl }: { crawl: CrawlView }) {
  const open = useCrawl((s) => s.journalOpen);
  const flash = useCrawl((s) => s.flash);
  const setOpen = useCrawl((s) => s.setJournal);
  const [shown, setShown] = useState<number>();
  const [book, setBook] = useState(false);
  const lit = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!flash || lit.current === flash.at) return;
    lit.current = flash.at;
    setShown(flash.lore);
  }, [flash]);
  const read = crawl.lore.filter((l) => l.read).length;
  const fresh = flash && deviceNow() - flash.at < 6000 ? flash.lore : undefined;
  return (
    <div className="fk-hud__crawl-journal" data-testid="crawl-journal" data-read={read} data-total={crawl.lore.length}>
      <div className="fk-hud__crawl-jhead">
        <button type="button" className="fk-hud__crawl-jtoggle" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="crawl-journal-toggle">
          {open ? '▾' : '▸'} Journal <span className="fk-hud__crawl-count">{read} / {crawl.lore.length} read</span>
        </button>
        {read > 0 && <Button small variant="ghost" onClick={() => setBook(true)} data-testid="crawl-journal-book">Read all</Button>}
      </div>
      {open && (
        <ul className="fk-hud__crawl-lore">
          {crawl.lore.map((l, i) => (
            <li key={i} className={`fk-hud__crawl-entry ${l.read ? 'is-read' : 'is-unread'} ${fresh === i ? 'is-fresh' : ''}`} data-testid="crawl-lore" data-read={l.read}>
              {l.read ? (
                <button type="button" className="fk-hud__crawl-etitle" onClick={() => setShown(shown === i ? undefined : i)} aria-expanded={shown === i}>{cap(l.title)}</button>
              ) : (
                <span className="fk-hud__crawl-etitle">{l.title ? cap(l.title) : 'A fragment'} <i>unread</i></span>
              )}
              {l.read && shown === i && <q className="fk-hud__crawl-etext">{l.text}</q>}
            </li>
          ))}
          {!crawl.lore.length && <li className="fk-hud__crawl-entry is-unread">Nothing is written here.</li>}
        </ul>
      )}
      <Modal open={book} onClose={() => setBook(false)} eyebrow="The journal" title={crawl.name || 'The crawl'} testId="crawl-journal-modal" size="wide">
        <div className="fk-crawl-book">
          {crawl.lore.map((l, i) => (
            <article key={i} className={`fk-crawl-book__entry ${l.read ? '' : 'is-unread'}`}>
              <h4>{l.title ? cap(l.title) : 'A fragment'}</h4>
              {l.read ? <p>{l.text}</p> : <p className="fk-crawl-book__unread">Unread — find it on the walls and Examine it.</p>}
            </article>
          ))}
        </div>
      </Modal>
    </div>
  );
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/**
 * docs/140 DT13: the crawl's briefing card. It opens on its own as a crawl begins (live, the first
 * turn the party plans is pulse 1, not 0) with what the storyteller set up: the crawl's name, the
 * quest it is a stop of, what happened here, why the party came, what wins it, and the first depth's
 * arrival words (the load's own banner for depth 1 never reaches the client). Dismissed with a
 * click; the crawl card's Briefing button opens it again.
 */
export function CrawlBriefing({ board }: { board?: Snapshot }) {
  const shown = useShownSnapshot();
  const encounterId = useEncounter((s) => s.encounterId);
  const open = useCrawl((s) => s.briefOpen);
  const briefedFor = useCrawl((s) => s.briefedFor);
  const snap = board ?? shown;
  const crawl = snap?.crawl;
  const key = encounterId !== undefined ? String(encounterId) : undefined;
  const fresh = !!crawl && !snap?.explore && (snap?.pulseIndex ?? 99) <= 1 && key !== undefined && briefedFor !== key;
  useEffect(() => {
    if (!fresh || key === undefined) return;
    let seen = false;
    try { seen = sessionStorage.getItem(`fk.crawlBrief.${key}`) === '1'; sessionStorage.setItem(`fk.crawlBrief.${key}`, '1'); } catch { /* private mode */ }
    useCrawl.getState().setBrief(!seen, key);
  }, [fresh, key]);
  if (!snap || !crawl || snap.explore) return null;
  const dweller = crawl.dweller !== undefined ? snap.units.find((u) => u.unitId === crawl.dweller)?.name : snap.bossName;
  const b = briefingOf(crawl, dweller);
  return (
    <Modal open={open} onClose={() => useCrawl.getState().setBrief(false)} eyebrow={b.quest ? 'A stop on the quest' : 'The crawl'} title={b.name} testId="crawl-briefing">
      <div className="fk-crawl-brief" data-testid="crawl-briefing-body">
        {b.quest && <p className="fk-crawl-brief__quest" data-testid="crawl-briefing-quest">{b.quest}</p>}
        {b.premise && <p className="fk-crawl-brief__premise" data-testid="crawl-briefing-premise">{b.premise}</p>}
        {b.hook && <p className="fk-crawl-brief__hook" data-testid="crawl-briefing-hook"><b>Why you are here.</b> {b.hook}</p>}
        <p className="fk-crawl-brief__goal" data-testid="crawl-briefing-goal"><b>To win.</b> {b.goal}</p>
        {b.depth && (
          <div className="fk-crawl-brief__depth" data-testid="crawl-briefing-depth">
            <span className="fk-hud__crawl-eyebrow">Depth 1 · you arrive at</span>
            <b>{cap(b.depth.name)}</b>
            {b.depth.text && <q>{b.depth.text}</q>}
          </div>
        )}
        <p className="fk-crawl-brief__warn">Watch your step: the dark holds more than foes. A search may find a plate or a wire before it finds you.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Button onClick={() => useCrawl.getState().setBrief(false)} data-testid="crawl-briefing-go">Into the dark</Button></div>
      </div>
    </Modal>
  );
}
